import React, { useEffect } from "react";
import ThriveCaseSelection from "../common/ThriveCaseSelection";
import Tasks from "./EventList/Tasks";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import ActivityBarChart from "./ActivityBarChart";
import { axiosInstance, fetchInstance } from "../../utils/networking";
import dayjs from "dayjs";
import { UserContext } from "../../context/UserContext";
import ThriveModal from "../common/ThriveModal";
import CloseIcon from "@mui/icons-material/Close";
import IDEACoreCRM from "../IDEACoreCRM/IDEACoreCRM";
import {
  reorderUsers,
  userStatsSummary,
} from "./ProgramStatistics/ProgramStatistics";
interface EngagementsDashboardProps {}

const EngagementsDashboard: React.FC<EngagementsDashboardProps> = ({}) => {
  const [crmFirms, setCrmFirms] = React.useState<any[]>([]);
  const [crmActivities, setCrmActivities] = React.useState<any[]>([]);
  const [crmOpenCases, setCrmOpenCases] = React.useState<any[]>([]);
  const [crmPeriod, setCrmPeriod] = React.useState<string>("2w");
  const [cardOpen, setCardOpen] = React.useState<boolean>(false);
  const [allCaseCardOpen, setAllCaseCardOpen] = React.useState<boolean>(false);
  const [userStats, setUserStats] = React.useState<any>({});
  const { user } = React.useContext(UserContext);
  const crmLoadedPeriodRef = React.useRef<string | null>(null);

  useEffect(() => {
    axiosInstance
      .get(
        `/user/allUserStats?startDate=${dayjs()
          .dayOfYear(1)
          .format("YYYY-MM-DD")}&endDate=${""}`
      )
      .then((res) => {
        if (user.role == "admin") {
          setUserStats(userStatsSummary(reorderUsers(res.data.userStats)));
        } else {
          let sbcStatus = res.data.userStats.find(
            (item: any) => item.email === user.email
          );

          setUserStats(sbcStatus);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch user stats:", err);
      });
  }, []);

  const fetchCRMData = async (period: string) => {
    let stream_cases: any[] = [];
    const response = await fetchInstance(
      `${process.env.REACT_APP_API_DOMAIN}/cases/recent`,
      {
        method: "POST",
        body: JSON.stringify({
          period,
          case_status: "open",
          sortBy: "recent",
        }),
      }
    );
    if (!response.body) {
      throw new Error("ReadableStream not supported in this environment.");
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (line.trim()) {
          try {
            stream_cases.push(JSON.parse(line));
          } catch (err) {
            console.error("Failed to parse line:", line, err);
          }
        }
      }
    }

    setCrmOpenCases(stream_cases);

    if (stream_cases.length > 0) {
      const case_id = stream_cases.map((item: any) => item._id).join(",") + ",";

      axiosInstance
        .post(`/activity/massive_get`, { case_id })
        .then((res) => setCrmActivities(res.data.activities));

      const firmIds = stream_cases.map((c: any) => c.firmRecord._id);
      const firmResponse = await fetchInstance(
        `${process.env.REACT_APP_API_DOMAIN}/firms/massive_get`,
        {
          method: "POST",
          body: JSON.stringify({ ids: firmIds }),
        }
      );
      if (!firmResponse.body) {
        throw new Error("ReadableStream not supported in this environment.");
      }
      const firmReader = firmResponse.body.getReader();
      const firmDecoder = new TextDecoder("utf-8");
      let firmBuffer = "";
      let stream_firms: any[] = [];

      while (true) {
        const { value, done } = await firmReader.read();
        if (done) break;

        firmBuffer += firmDecoder.decode(value, { stream: true });
        const lines = firmBuffer.split("\n");
        firmBuffer = lines.pop() || "";

        for (const line of lines) {
          if (line.trim()) {
            try {
              stream_firms.push(JSON.parse(line));
            } catch (err) {
              console.error("Failed to parse line:", line, err);
            }
          }
        }
      }
      setCrmFirms(stream_firms);
    }
  };

  // Lazy-load CRM modal data only when the modal is opened for a new period
  useEffect(() => {
    if (cardOpen && crmLoadedPeriodRef.current !== crmPeriod) {
      crmLoadedPeriodRef.current = crmPeriod;
      fetchCRMData(crmPeriod);
    }
  }, [cardOpen]);

  const handleCrmPeriodChange = (event: any) => {
    const newPeriod = event.target.value;
    console.log("Selected CRM Period:", newPeriod);
    setCrmPeriod(newPeriod);
    setCrmOpenCases([]);
    setCrmActivities([]);
    setCrmFirms([]);
    crmLoadedPeriodRef.current = newPeriod;
    fetchCRMData(newPeriod);
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <div className=" col-span-1">
        <div className="rounded-2xl shadow-md inset-0">
          <Tasks></Tasks>
        </div>
      </div>
      <div className="col-span-1 flex flex-col justify-between">
        {/*Below div is for program stats */}
        <div className="rounded-2xl shadow-md inset-0">
          <Box
            sx={{
              textAlign: "center",
              backgroundColor:
                process.env.REACT_APP_API_DOMAIN !=
                "https://www.thrive-drive.org:8443"
                  ? "rgb(63, 93, 125)"
                  : "rgb(158, 31, 99)",
              padding: "3px",
              margin: 0,
              color: "white",
              borderRadius: "15px 15px 0 0",
            }}
          >
            <Typography sx={{ display: "inline-block" }} variant="h6">
              {user.role === "admin" ? "Program Stats" : "Your Stats"}
            </Typography>
          </Box>
          <div className="h-[300px]">
            <ActivityBarChart data={userStats.userStats} />
          </div>
        </div>

        {/*Below div is for case grid */}
        <div className="rounded-2xl shadow-md inset-0 h-[290px]">
          <Box
            onClick={() => {
              setCardOpen(true);
            }}
            sx={{ cursor: "pointer" }}
          >
            <Box
              sx={{
                textAlign: "center",
                backgroundColor:
                  process.env.REACT_APP_API_DOMAIN !=
                  "https://www.thrive-drive.org:8443"
                    ? "rgb(63, 93, 125)"
                    : "rgb(158, 31, 99)",
                padding: "3px",
                margin: 0,
                color: "white",
                borderRadius: "15px 15px 0 0",
              }}
            >
              <Typography sx={{ display: "inline-block" }} variant="h6">
                Case Grid
              </Typography>
            </Box>
            <Box
              component="img"
              src={`${process.env.REACT_APP_API_DOMAIN}/images/crm.png`}
              alt="crm"
              sx={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
                borderRadius: "0 0 15px 15px",
              }}
            />
          </Box>
        </div>
      </div>

      {/* <div className="rounded-lg shadow-md inset-0">
              <OpenCases />
            </div> */}
      <div className="col-span-1">
        <div className="rounded-lg shadow-md inset-0">
          <Box
            sx={{
              textAlign: "center",
              backgroundColor:
                process.env.REACT_APP_API_DOMAIN !=
                "https://www.thrive-drive.org:8443"
                  ? "rgb(63, 93, 125)"
                  : "rgb(158, 31, 99)",
              padding: "3px",
              margin: 0,
              color: "white",
              borderRadius: "15px 15px 0 0",
            }}
          >
            <Typography sx={{ display: "inline-block" }} variant="h6">
              Recent Cases (2 weeks)
            </Typography>
          </Box>
          <Button
            sx={{ width: "100%" }}
            onClick={() => {
              setAllCaseCardOpen(true);
            }}
          >
            {" "}
            View All Cases
          </Button>
          <ThriveCaseSelection
            height={670}
            cases={[]}
            reloadCases={true}
            checkedCases={[]}
            setCheckedCases={() => {}}
            selectedCase={{}}
            setSelectedCase={() => {}}
            enableControl={false}
            enableSelection={false}
            enableMultiSelection={false}
            title=""
            status_default="either"
            sortby_default="recent"
            period_default="2w"
            setOpen={() => {}}
            handleNext={() => {}}
          ></ThriveCaseSelection>
        </div>
      </div>
      <ThriveModal
        width={1280}
        open={cardOpen}
        setOpen={setCardOpen}
        contentComponent={
          <Box>
            <Box
              sx={{
                textAlign: "center",
                backgroundColor: "#9E1F63FF",
                padding: "3px",
                margin: 0,
                color: "white",
                borderRadius: "5px 5px 0 0",
                marginBottom: "20px",
              }}
            >
              <Typography sx={{ display: "inline-block" }} variant="h4">
                Current Cases
              </Typography>
              <Box
                sx={{
                  display: "inline",
                  float: "right",
                  cursor: "pointer",
                }}
                onClick={() => {
                  setCardOpen(false);
                }}
              >
                <CloseIcon sx={{ fontSize: 40 }}></CloseIcon>
              </Box>
            </Box>
            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <FormControl sx={{ minWidth: 240, marginBottom: "20px" }}>
                <InputLabel id="crm-period-select-label">
                  Time Period
                </InputLabel>
                <Select
                  labelId="crm-period-select-label"
                  id="crm-period-select"
                  value={crmPeriod}
                  label="Time Period"
                  onChange={handleCrmPeriodChange}
                >
                  <MenuItem value={"1d"}>1 Day</MenuItem>
                  <MenuItem value={"1w"}>1 Week</MenuItem>
                  <MenuItem value={"2w"}>2 Weeks</MenuItem>
                  <MenuItem value={"1m"}>1 Month</MenuItem>
                  <MenuItem value={"2m"}>2 Months</MenuItem>
                  <MenuItem value={"all"}>All</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <IDEACoreCRM
              openCases={crmOpenCases}
              caseActivities={crmActivities}
              firms={crmFirms}
            ></IDEACoreCRM>
          </Box>
        }
      ></ThriveModal>
      <ThriveModal
        height={700}
        open={allCaseCardOpen}
        contentComponent={
          <>
            <ThriveCaseSelection
              height={500}
              cases={[]}
              reloadCases={true}
              checkedCases={[]}
              setCheckedCases={() => {}}
              selectedCase={{}}
              setSelectedCase={() => {}}
              enableControl={true}
              enableSelection={false}
              enableMultiSelection={false}
              setOpen={setAllCaseCardOpen}
              title={"All Cases"}
              status_default="either"
              sortby_default="recent"
              period_default="all"
              handleNext={() => {}}
            ></ThriveCaseSelection>
          </>
        }
      />
    </div>
  );
};

export default React.memo(EngagementsDashboard);
